/** AI summary display block — clamp-2, expand toggle, state variants */

import { useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'

interface SummaryBlockProps {
  summary: string | null
  status: string | null
  extractionMethod: string | null
  /** MIME type — images always render the summary block regardless of extractionMethod. */
  contentType?: string | null
  /** Ignored: colours come from theme tokens. Kept so legacy callers still compile. */
  isDark?: boolean
  /** Called when user clicks "Generate" (summary is null + status completed) */
  onGenerate?: () => void
  /** True while regenerate mutation is in-flight */
  generatePending?: boolean
}

export function SummaryBlock({
  summary,
  status,
  extractionMethod,
  contentType,
  onGenerate,
  generatePending,
}: SummaryBlockProps) {
  const t = useT()
  const [expanded, setExpanded] = useState(false)
  const isImage = contentType?.startsWith('image/') ?? false

  // Hide for unsupported file types — except images, which use the vision pipeline
  // and may carry a historical extractionMethod='unsupported' from before the
  // auto-vision-summarize flow shipped.
  if (extractionMethod === 'unsupported' && !isImage) return null

  // Processing skeleton
  if (status === 'processing') {
    return (
      <div className="min-h-[2.5rem] px-3 pb-2">
        <div className="flex items-center gap-1.5 text-xs text-fg-subtle">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          <span>{t('storage.summary.generating')}</span>
        </div>
      </div>
    )
  }

  // No summary yet but extraction completed — offer Generate
  if (!summary && status === 'completed') {
    return (
      <div className="min-h-[2.5rem] px-3 pb-2">
        <button
          type="button"
          onClick={onGenerate}
          disabled={generatePending}
          className="flex cursor-pointer items-center gap-1 text-xs text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-subtle disabled:no-underline"
        >
          {generatePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
          {generatePending ? t('storage.summary.generating') : t('storage.summary.generate')}
        </button>
      </div>
    )
  }

  // No summary (pending/failed/null status)
  if (!summary) {
    return <div className="min-h-[2.5rem]" aria-hidden="true" />
  }

  // Loaded summary
  return (
    <div className="min-h-[2.5rem] px-3 pb-2">
      <p className={cn('text-xs leading-relaxed text-fg-muted', !expanded && 'line-clamp-2')}>{summary}</p>
      {summary.length > 120 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-0.5 cursor-pointer text-xs text-accent-text hover:underline"
        >
          {expanded ? t('storage.summary.collapse') : t('storage.summary.expand')}
        </button>
      )}
    </div>
  )
}
